import { objectMergeKeys } from '@/features/shared/tools'
import { PropertyStatus, type CheckedPropertyStatus, type Status } from './PropertyStatus'
import type {
  PropertyConfigurationModel,
  PropertyStatusArrayConfigurationCallback,
  PropertyStatusConfiguration,
  PropertyStatusConfigurationCallback,
} from './PropertyStatusConfiguration'
import { isFunction } from '@/features/shared/tools/function.ts'

export class PropertyStatusChecker<TModel extends object, TContext, TGlobalModel = TModel> {

  #regles: PropertyStatusConfiguration<TModel, TContext, TGlobalModel> &
    PropertyConfigurationModel<TModel, any, TContext, TGlobalModel>

  constructor(regles: PropertyStatusConfiguration<TModel, TContext, TGlobalModel>) {
    this.#regles = regles
  }

  checkStatus(model: TModel, context: TContext): Status<TModel> {
    return this.checkStatusInternal(model, model as any, context)
  }

  private checkStatusInternal(
    model: TModel,
    globalModel: TGlobalModel,
    context: TContext,
    stack?: Array<object>,
    index?: number,
    parent?: any,
    parentStatus?: PropertyStatus
  ): Status<TModel> {
    // Controle des références circulaires
    if (stack?.some((item) => item === model) === true) {
      throw Error('Référence circulaire detectée')
    }
    const localStack = stack ? [...stack, model] : [model]

    // Statut du model
    const modelStatus = calculeStatus$model({
      model,
      index,
      model$StatusOuCallback: this.#regles?.$model,
      parent,
      parent$Status: parentStatus,
      globalModel,
      context,
    })

    // Fusionne les clés du model et des régles
    const modelKeys = objectMergeKeys(model, this.#regles ?? {})
      .filter(key => !["$model", "$item"].includes(key))
    for (const key of modelKeys) {

      // Valeur et regles du membre
      const modelValue = (model as any)[key]
      const modelRegle = (this.#regles as any)?.[key]

      if (Array.isArray(modelValue)) {
        // Statut de l'array
        const arrayStatus = calculeStatus$model({
          model: modelValue,
          index,
          model$StatusOuCallback: modelRegle?.$model,
          parent: model,
          parent$Status: modelStatus.$status,
          globalModel,
          context,
        })

        // Pour chaque instance du tableau execute un check
        const resultArray = Object.assign([], arrayStatus) as Array<any> & CheckedPropertyStatus

        // Todo : Traiter array de types
        const checkerItem = new PropertyStatusChecker<object, TContext, TGlobalModel>(modelRegle)

        for (let index = 0; index < modelValue.length; index++) {
          const modelItem = modelValue[index]
          const itemStatus = calculeStatus$item({
            model: modelItem,
            index,
            item$StatusOuCallback: modelRegle?.$item,
            parent: modelValue,
            parent$Status: resultArray.$status,
            globalModel,
            context,
          })
          const resultItem = checkerItem.checkStatusInternal(
            modelItem,
            globalModel,
            context,
            localStack,
            index,
            modelValue,
            itemStatus.$status
          )

          // Applique le status de l'item à l'item
          Object.assign(resultItem, itemStatus)

          resultArray.push(resultItem)
        }
        ; (modelStatus as any)[key] = resultArray
      } else if (modelValue instanceof Object || typeof modelRegle === 'object') {
        const checkerEnfant = new PropertyStatusChecker<object, TContext, TGlobalModel>(modelRegle ?? {})
          ; (modelStatus as any)[key] = checkerEnfant.checkStatusInternal(
            modelValue ?? {},
            globalModel,
            context,
            localStack,
            undefined,
            model,
            modelStatus.$status
          )
      } else {
        if (modelStatus.$status === PropertyStatus.NotPresent) {
          (modelStatus as any)[key] = { $status: PropertyStatus.NotPresent } as CheckedPropertyStatus
          continue;
        }
        const regleMembre = modelRegle as
          | PropertyStatus
          | PropertyStatusConfigurationCallback<TModel, any, TContext, TGlobalModel>
          | PropertyStatusArrayConfigurationCallback<TModel, any, TContext, TGlobalModel>
        if (isFunction(regleMembre)) {
          if (index !== undefined) {
            const callback = regleMembre as PropertyStatusArrayConfigurationCallback<
              TModel,
              any,
              TContext,
              TGlobalModel
            >
            const result: CheckedPropertyStatus = {
              $status: callback(modelValue, index, model, globalModel, context) ?? PropertyStatus.Optional,
            }
              ; (modelStatus as any)[key] = result
          } else {
            const callback = regleMembre as PropertyStatusConfigurationCallback<TModel, any, TContext, TGlobalModel>
            const result: CheckedPropertyStatus = {
              $status: callback(modelValue, model, globalModel, context) ?? PropertyStatus.Optional,
            }
              ; (modelStatus as any)[key] = result
          }
        } else if (typeof regleMembre === 'number') {
          const result: CheckedPropertyStatus = {
            $status: regleMembre,
          }
            ; (modelStatus as any)[key] = result
        } else {
          const result: CheckedPropertyStatus = { $status: PropertyStatus.Optional }
            ; (modelStatus as any)[key] = result
        }
      }
    }

    return modelStatus as Status<TModel>
  }
}

function calculeStatus$model<TModel extends object, TGlobalModel>(option: ColculeStatusModelOptions<TModel, TGlobalModel>): CheckedPropertyStatus {
  const { model, index, parent, globalModel, context, model$StatusOuCallback, parent$Status: parentStatus } = option

  if (parentStatus === PropertyStatus.NotPresent) return { $status: PropertyStatus.NotPresent }
  if (index !== undefined) return {}

  switch (typeof model$StatusOuCallback) {
    case "number": return { $status: model$StatusOuCallback }
    case "function":
      return {
        $status: model$StatusOuCallback.call(null, model, parent, globalModel, context) as PropertyStatus
      }
    default: return { $status: PropertyStatus.Optional }
  }
}

function calculeStatus$item<TModel extends object, TGlobalModel>(option: ColculeStatusItemOptions<TModel, TGlobalModel>): CheckedPropertyStatus {
  const { model, index, parent, globalModel, context, item$StatusOuCallback, parent$Status: parentStatus } = option

  if (parentStatus === PropertyStatus.NotPresent) return { $status: PropertyStatus.NotPresent }

  switch (typeof item$StatusOuCallback) {
    case "number": return { $status: item$StatusOuCallback }
    case "function":
      return {
        $status: item$StatusOuCallback.call(null, model, index, parent, globalModel, context) as PropertyStatus
      }
    default: return { $status: PropertyStatus.Optional }
  }
}


type ColculeStatusModelOptions<TModel extends object, TGlobalModel> = {
  model: TModel,
  index?: number,
  parent: any,
  globalModel: TGlobalModel,
  context: any,
  model$StatusOuCallback?: PropertyStatusConfigurationCallback<TModel, any, any, TGlobalModel> | PropertyStatus
  parent$Status?: PropertyStatus,
}

type ColculeStatusItemOptions<TModel extends object, TGlobalModel> = {
  model: TModel,
  index: number,
  parent: any,
  globalModel: TGlobalModel,
  context: any,
  item$StatusOuCallback?: PropertyStatusArrayConfigurationCallback<TModel, any, any, TGlobalModel> | PropertyStatus
  parent$Status?: PropertyStatus,
}
